<template>
  <div class="mb-landing">
    <!-- Continue banner for returning users -->
    <div
      v-if="savedService"
      class="mb-landing__continue-banner"
      role="region"
      aria-label="Servicio guardado"
    >
      <div
        class="flex flex-col sm:flex-row items-start sm:items-center gap-8 sm:gap-16"
      >
        <p class="text-sm">
          <span class="font-semibold">{{ copy.continueBanner.label }}</span>
          {{ savedService.name }}
        </p>
        <div class="flex gap-8">
          <button
            type="button"
            class="mb-btn text-sm"
            @click="continueToService"
          >
            {{ copy.continueBanner.cta }}
            <LucideArrowRight :size="14" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="mb-btn text-sm"
            @click="scrollToSelector"
          >
            {{ copy.continueBanner.change }}
          </button>
        </div>
      </div>
    </div>

    <!-- Hero -->
    <section class="mb-landing__hero">
      <div class="mb-landing__hero-inner">
        <h1 class="text-2xl font-bold leading-tight mb-16">
          {{ copy.hero.title }}
        </h1>
        <p class="text-base leading-relaxed mb-20">
          {{ copy.hero.subtitle }}
        </p>
        <div class="flex flex-row flex-wrap sm:flex-row gap-x-12 gap-y-8 mb-20">
          <button
            type="button"
            class="mb-btn justify-center text-sm w-full"
            @click="scrollToSelector"
          >
            {{ copy.hero.ctaPrimary }}
            <LucideChevronDown :size="14" aria-hidden="true" />
          </button>
          <NuxtLink
            :to="{ path: '/', hash: '#servicios' }"
            class="mb-btn justify-center text-sm w-full"
            @click.prevent="scrollToSupported"
          >
            {{ copy.hero.ctaSecondary }}
          </NuxtLink>
        </div>
        <p class="text-xs text-gray-600 dark:text-gray-400">
          {{ copy.hero.microcopy }}
        </p>
      </div>
    </section>

    <!-- Service Selector -->
    <section
      id="selector"
      ref="selectorRef"
      class="mb-landing__section mb-landing__selector"
      aria-labelledby="selector-heading"
    >
      <h2 id="selector-heading" class="text-xl font-bold mb-8">
        {{ copy.selector.title }}
      </h2>
      <p class="text-sm text-gray-700 dark:text-gray-300 mb-24">
        {{ copy.selector.subtitle }}
      </p>

      <div class="mb-landing__service-grid">
        <article
          v-for="svc in services"
          :key="svc.slug"
          class="mb-landing__service-card"
        >
          <div class="mb-landing__service-card-header">
            <h3 class="text-base font-semibold">
              {{ svc.name }}
            </h3>
            <span class="mb-landing__badge">{{ svc.type }}</span>
          </div>
          <p class="mb-landing__service-region">{{ svc.region }}</p>
          <p class="mb-landing__service-description">
            {{ svc.description }}
          </p>
          <div class="mb-landing__service-actions">
            <NuxtLink
              :to="svc.href"
              class="mb-btn text-xs"
              :data-testid="`landing-service-${svc.slug}`"
              :aria-label="`${copy.selector.viewSchedule} de ${svc.name}`"
              @click="selectService($event, svc.slug)"
            >
              {{ copy.selector.viewSchedule }}
              <LucideArrowRight :size="14" aria-hidden="true" />
            </NuxtLink>
          </div>
        </article>
      </div>
    </section>

    <!-- About -->
    <section
      class="mb-landing__section mb-landing__about"
      aria-labelledby="about-heading"
    >
      <h2 id="about-heading" class="text-xl font-bold mb-16">
        {{ copy.about.title }}
      </h2>
      <p class="text-base leading-relaxed mb-24">
        {{ copy.about.body }}
      </p>

      <div class="mb-landing__benefits-grid">
        <div
          v-for="(benefit, i) in copy.about.benefits"
          :key="i"
          class="mb-landing__benefit-card"
        >
          <h3 class="text-sm font-semibold mb-8">
            {{ benefit.title }}
          </h3>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            {{ benefit.text }}
          </p>
        </div>
      </div>
    </section>

    <!-- Supported Services -->
    <section
      id="servicios"
      ref="supportedRef"
      class="mb-landing__section mb-landing__supported"
      aria-labelledby="supported-heading"
    >
      <h2 id="supported-heading" class="text-xl font-bold mb-8">
        {{ copy.supported.title }}
      </h2>
      <p class="text-sm text-gray-700 dark:text-gray-300 mb-24">
        {{ copy.supported.intro }}
      </p>

      <ul class="mb-landing__supported-list">
        <li
          v-for="svc in services"
          :key="svc.slug"
          class="mb-landing__supported-item"
        >
          <NuxtLink
            :to="svc.href"
            class="mb-landing__supported-link"
            @click="selectService($event, svc.slug)"
          >
            <span class="font-semibold">{{ svc.name }}</span>
            <span class="text-sm text-gray-600 dark:text-gray-400">
              {{ svc.description }}
            </span>
          </NuxtLink>
        </li>
      </ul>
    </section>

    <!-- How It Works -->
    <section
      class="mb-landing__section mb-landing__how"
      aria-labelledby="how-heading"
    >
      <h2 id="how-heading" class="text-xl font-bold mb-16">
        {{ copy.howItWorks.title }}
      </h2>
      <ol class="mb-landing__steps">
        <li
          v-for="(step, i) in copy.howItWorks.steps"
          :key="i"
          class="mb-landing__step"
        >
          <span class="mb-landing__step-number" aria-hidden="true">{{
            i + 1
          }}</span>
          <span class="text-sm">{{ step }}</span>
        </li>
      </ol>
    </section>

    <!-- Disclaimer -->
    <section
      class="mb-landing__section mb-landing__disclaimer"
      aria-labelledby="disclaimer-heading"
    >
      <h2 id="disclaimer-heading" class="text-xl font-bold mb-16">
        {{ copy.disclaimer.title }}
      </h2>
      <p class="text-sm leading-relaxed">
        {{ copy.disclaimer.body }}
      </p>
    </section>

    <!-- FAQ -->
    <section
      class="mb-landing__section mb-landing__faq"
      aria-labelledby="faq-heading"
    >
      <h2 id="faq-heading" class="text-xl font-bold mb-16">
        {{ copy.faq.title }}
      </h2>
      <div class="mb-landing__faq-list">
        <details
          v-for="(item, i) in copy.faq.items"
          :key="i"
          class="mb-landing__faq-item"
        >
          <summary class="mb-landing__faq-question">
            <span class="text-sm font-semibold">{{ item.question }}</span>
            <LucideChevronDown
              :size="16"
              aria-hidden="true"
              class="mb-landing__faq-chevron"
            />
          </summary>
          <p class="mb-landing__faq-answer">
            {{ item.answer }}
          </p>
        </details>
      </div>
    </section>

    <!-- Footer -->
    <footer class="mb-landing__footer">
      <div class="mb-landing__footer-brand">
        <strong class="block text-base mb-4">{{ copy.footer.brand }}</strong>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-16">
          {{ copy.footer.tagline }}
        </p>
      </div>

      <nav aria-label="Enlaces del sitio">
        <ul class="mb-landing__footer-links">
          <li>
            <NuxtLink to="/" class="text-sm">Inicio</NuxtLink>
          </li>
          <li v-for="svc in services" :key="svc.slug">
            <NuxtLink :to="svc.href" class="text-sm">
              {{ svc.name }}
            </NuxtLink>
          </li>
        </ul>
      </nav>

      <p
        class="text-xs text-gray-500 dark:text-gray-500 mt-20 pt-20 border-t border-gray-300 dark:border-gray-700"
      >
        {{ copy.footer.disclaimer }}
      </p>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { usePageSeo } from '~~/composables/usePageSeo';
import { getLandingServices, LANDING_COPY } from '~~/helpers/landing';
import { useUserStore } from '~~/stores/userconfig';
import {
  BASE_SERVICIO_SLUG,
  BASE_SITE,
  type TServiciosSlug,
} from '~~/constants';

const router = useRouter();
const userStore = useUserStore();

const services = getLandingServices();
const copy = LANDING_COPY;

const selectorRef = ref<HTMLElement | null>(null);
const supportedRef = ref<HTMLElement | null>(null);

const savedService = computed(() => {
  const slug = userStore.savedServicio;
  if (!slug) return undefined;
  return services.find((s) => s.slug === slug);
});

const scrollToSelector = () => {
  selectorRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const scrollToSupported = () => {
  supportedRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const continueToService = () => {
  const slug = userStore.savedServicio || BASE_SERVICIO_SLUG;
  router.push(`/${slug}`);
};

const selectService = (event: MouseEvent, slug: TServiciosSlug) => {
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  event.preventDefault();
  void userStore.setPreferencia({ key: 'servicio', value: slug });
};

// SEO
usePageSeo();

useSeoMeta({
  title: `${BASE_SITE.TITLE} | Horarios de trenes en Chile`,
  description:
    'Consulta horarios y servicios de trenes en Chile de forma rápida. Elige tu servicio principal, revisa recorridos disponibles y accede a información útil para planificar tu viaje.',
  ogTitle: `${BASE_SITE.TITLE} | Horarios de trenes en Chile`,
  ogDescription:
    'Consulta horarios y servicios de trenes en Chile de forma rápida. Elige tu servicio principal, revisa recorridos disponibles y accede a información útil para planificar tu viaje.',
  twitterTitle: `${BASE_SITE.TITLE} | Horarios de trenes en Chile`,
  twitterDescription:
    'Consulta horarios y servicios de trenes en Chile de forma rápida. Elige tu servicio principal, revisa recorridos disponibles y accede a información útil para planificar tu viaje.',
});

useHead(() => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: copy.faq.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Servicios disponibles en ${BASE_SITE.NAME}`,
    itemListElement: services.map((svc, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: svc.name,
      description: svc.description,
      url: `${BASE_SITE.URL}${svc.href}`,
    })),
  };

  return {
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(faqSchema),
        key: 'faq-schema',
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(itemListSchema),
        key: 'services-schema',
      },
    ],
  };
});
</script>

<style scoped>
@reference '../assets/css/tailwind.css';

.mb-landing {
  @apply px-16 pt-20 pb-100 min-h-screen;
}

.mb-landing__continue-banner {
  @apply mb-24 p-16 rounded-sm border border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800;
}

.mb-landing__hero {
  @apply mb-40;
}

.mb-landing__hero-inner {
  @apply w-full;
}

.mb-landing__section {
  @apply mb-40 pb-40 border-b border-gray-300 dark:border-gray-700;
}

.mb-landing__section:last-of-type {
  @apply border-b-0;
}

.mb-landing__service-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-16;
}

.mb-landing__service-card {
  @apply border border-gray-300 dark:border-gray-700 rounded-sm p-16 flex flex-col;
}

.mb-landing__service-card-header {
  @apply flex items-start justify-between gap-8 mb-8;
}

.mb-landing__badge {
  @apply text-xs uppercase tracking-wider px-8 py-4 rounded-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 whitespace-nowrap;
}

.mb-landing__service-region {
  @apply text-xs text-gray-600 dark:text-gray-400 mb-8;
}

.mb-landing__service-description {
  @apply text-sm text-gray-700 dark:text-gray-300 mb-16 flex-1;
}

.mb-landing__service-actions {
  @apply flex flex-wrap gap-8;
}

.mb-landing__benefits-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 gap-16;
}

.mb-landing__benefit-card {
  @apply border border-gray-300 dark:border-gray-700 rounded-sm p-16;
}

.mb-landing__supported-list {
  @apply grid grid-cols-1 sm:grid-cols-2 gap-12;
}

.mb-landing__supported-item {
  @apply border border-gray-300 dark:border-gray-700 rounded-sm;
}

.mb-landing__supported-link {
  @apply block p-12 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors;
}

.mb-landing__supported-link span {
  @apply block;
}

.mb-landing__steps {
  @apply space-y-16;
}

.mb-landing__step {
  @apply flex items-start gap-12;
}

.mb-landing__step-number {
  @apply flex-shrink-0 w-28 h-28 flex items-center justify-center rounded-full bg-orange text-orange-950 text-sm font-bold;
}

.mb-landing__disclaimer {
  @apply bg-gray-50 dark:bg-gray-900/50 p-20 rounded-sm;
}

.mb-landing__faq-list {
  @apply space-y-12;
}

.mb-landing__faq-item {
  @apply border border-gray-300 dark:border-gray-700 rounded-sm;
}

.mb-landing__faq-question {
  @apply flex items-center justify-between gap-16 p-16 cursor-pointer list-none;
}

.mb-landing__faq-chevron {
  @apply flex-shrink-0 transition-transform;
}

.mb-landing__faq-item[open] .mb-landing__faq-chevron {
  @apply rotate-180;
}

.mb-landing__faq-answer {
  @apply px-16 pb-16 text-sm text-gray-700 dark:text-gray-300;
}

.mb-landing__footer {
  @apply pt-40 border-t border-gray-300 dark:border-gray-700;
}

.mb-landing__footer-links {
  @apply grid grid-cols-2 sm:grid-cols-3 gap-12;
}

.mb-landing__footer-links a {
  @apply text-gray-700 dark:text-gray-300 hover:text-orange dark:hover:text-orange transition-colors;
}
</style>
